import { createFileRoute } from "@tanstack/react-router";
import { BUSINESS, SERVICES } from "@/lib/site-data";
import { useState } from "react";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: `Contact ${BUSINESS.shortName} — Free Trades SEO Audit` },
      {
        name: "description",
        content: `Talk to ${BUSINESS.shortName} about SEO, Google Ads, and websites for your trades company. Call ${BUSINESS.phone} or request a free audit from our Lehi, UT office.`,
      },
      { property: "og:title", content: `Contact ${BUSINESS.shortName}` },
      {
        property: "og:description",
        content: `Free 30-minute audit of your site, GBP, and ad spend.`,
      },
      { property: "og:url", content: "/contact" },
    ],
    links: [{ rel: "canonical", href: "https://lehitradessaeo.com/contact" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ProfessionalService",
          name: BUSINESS.name,
          telephone: BUSINESS.phone,
          email: BUSINESS.email,
          address: {
            "@type": "PostalAddress",
            streetAddress: BUSINESS.address.street,
            addressLocality: BUSINESS.address.locality,
            addressRegion: BUSINESS.address.region,
            postalCode: BUSINESS.address.postalCode,
            addressCountry: BUSINESS.address.country,
          },
          openingHours: "Mo-Fr 08:00-18:00",
          contactPoint: {
            "@type": "ContactPoint",
            telephone: BUSINESS.phone,
            contactType: "sales",
            areaServed: "US-UT",
          },
        }),
      },
    ],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <main className="mx-auto max-w-6xl px-6 py-20">
      <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
        Get in touch
      </p>
      <h1 className="mt-4 font-display text-5xl tracking-tight md:text-6xl">
        Book a free trades SEO audit
      </h1>
      <p className="mt-5 max-w-2xl text-lg text-muted-foreground">
        Tell us about your company and we'll put together a 30-minute review of
        your website, Google Business Profile, and current ad spend — no pitch,
        no obligation.
      </p>

      <div className="mt-12 grid gap-8 md:grid-cols-3">
        <aside className="space-y-6 md:col-span-1">
          <div className="rounded-2xl border border-border bg-card p-6">
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Talk to us
            </p>
            <a
              href={BUSINESS.phoneHref}
              className="mt-3 block font-display text-3xl text-primary hover:underline"
            >
              {BUSINESS.phone}
            </a>
            <p className="mt-1 text-sm text-muted-foreground">{BUSINESS.hours}</p>
          </div>
          <address className="not-italic">
            <h2 className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
              Office
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              {BUSINESS.address.street}<br />
              {BUSINESS.address.locality}, {BUSINESS.address.region}{" "}
              {BUSINESS.address.postalCode}
            </p>
            <p className="mt-2 text-sm">
              <a href={`mailto:${BUSINESS.email}`} className="text-primary hover:underline">
                {BUSINESS.email}
              </a>
            </p>
            <p className="mt-4 text-xs text-muted-foreground">{BUSINESS.license}</p>
          </address>
        </aside>

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (submitting) return;
            setError(null);
            setSubmitting(true);
            const fd = new FormData(e.currentTarget);
            try {
              const res = await fetch("/api/public/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  name: String(fd.get("name") || ""),
                  phone: String(fd.get("phone") || ""),
                  email: String(fd.get("email") || ""),
                  service: String(fd.get("service") || ""),
                  message: String(fd.get("message") || ""),
                }),
              });
              if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(data?.error || "Something went wrong");
              }
              setSubmitted(true);
            } catch (err) {
              setError(err instanceof Error ? err.message : "Something went wrong");
            } finally {
              setSubmitting(false);
            }
          }}
          className="space-y-5 rounded-2xl border border-border bg-card p-8 md:col-span-2"
        >
          {submitted ? (
            <div className="rounded-xl border border-primary/30 bg-primary/5 p-6 text-center">
              <h2 className="font-display text-2xl">Thanks — message received.</h2>
              <p className="mt-2 text-sm text-muted-foreground">
                A dispatcher will call you back shortly. For emergencies, please
                call {BUSINESS.phone}.
              </p>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-medium">Name</span>
                  <input required name="name" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium">Phone</span>
                  <input required name="phone" type="tel" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary" />
                </label>
              </div>
              <label className="block">
                <span className="text-sm font-medium">Email</span>
                <input name="email" type="email" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary" />
              </label>
              <label className="block">
                <span className="text-sm font-medium">What do you need help with?</span>
                <select name="service" className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary">
                  <option value="">Pick a service…</option>
                  {SERVICES.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="text-sm font-medium">Tell us about your company</span>
                <textarea
                  name="message"
                  rows={5}
                  className="mt-2 w-full rounded-lg border border-input bg-background px-3 py-2 outline-none focus:border-primary"
                />
              </label>
              {error ? (
                <p className="text-sm text-destructive">{error}</p>
              ) : null}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Request my free audit"}
              </button>
            </>
          )}
        </form>
      </div>
    </main>
  );
}